import React, { useMemo, useState } from "react";

import { chain } from "lodash";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type GetCampaign_me_adObject } from "@north-beam/nb-client-side";
import { type Label } from "@north-beam/nb-common";
import {
  type IRelativePositionChoice,
  RelativePositionChoice,
} from "@pages/objects/common-touchpoints";
import { Select } from "@shared/selects";
import { Switch } from "@shared/switch";

import { Table } from "./journey-table";
import { aggregateRowsByLabelChoice } from "./utils";

interface Props {
  adObject: GetCampaign_me_adObject;
  touchpoints: any;
}

export const JourneyContent = ({ adObject, touchpoints }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [relativePosition, setRelativePosition] =
    useState<IRelativePositionChoice>("beforeOrAfter");
  const [isBreakdownSelectVisible, setIsBreakdownSelectVisible] =
    useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");

  const rootPathElement = touchpoints.grandTotal.pathElements[0];

  const adKeyHashToLabels: Record<string, Label[]> = useMemo(
    () => ({
      ...touchpoints.adKeyHashToLabels,
      [rootPathElement.elementId]: adObject.labelsDigest,
    }),
    [touchpoints, rootPathElement, adObject],
  );

  const labelOtions = useMemo(() => {
    const keys: Record<string, number> = {};

    for (const labelses of Object.values(adKeyHashToLabels)) {
      for (const { key } of labelses) {
        if (!(key in keys)) {
          keys[key] = 0;
        }
        keys[key] += 1;
      }
    }

    const labelChoices = chain(keys)
      .toPairs()
      .sortBy((v) => v[1])
      .reverse()
      .map(([key, _]) => ({ value: key, label: key }))
      .value();
    return [{ value: "", label: "(none)" }, ...labelChoices];
  }, [adKeyHashToLabels]);

  const unaggregatedRows = useMemo(() => {
    let result = touchpoints.commonTouchpointsBeforeOrAfter;

    if (relativePosition === "before") {
      result = touchpoints.commonTouchpointsBefore;
    } else if (relativePosition === "after") {
      result = touchpoints.commonTouchpointsAfter;
    } else {
      result = touchpoints.commonTouchpointsBeforeOrAfter;
    }

    const idToPlatformLabel: Record<string, string> = {};

    for (const [key, labels] of Object.entries(adKeyHashToLabels)) {
      const platform = labels.find((e) => e.key === "Platform (Northbeam)");
      idToPlatformLabel[key] = platform?.value ?? "";
    }

    return result.map((e: any) => {
      const paths = [{ ...rootPathElement, root: true }, ...e.pathElements].map(
        (e) => {
          return { ...e, platform: idToPlatformLabel[e.elementId] };
        },
      );

      return { ...e, pathElements: paths };
    });
  }, [touchpoints, rootPathElement, adKeyHashToLabels, relativePosition]);

  const rows = aggregateRowsByLabelChoice(
    selectedLabel,
    unaggregatedRows,
    adKeyHashToLabels,
  );

  return (
    <>
      <div className="mt-8">
        <h4 className="text-sm mb-1">How it works</h4>
        <p className="text-xs text-gray+1 font-light">
          This view shows you which marketing efforts (“touchpoints”) assist
          this campaign. Each row in the table below represents a distinct group
          of touchpoints that all appeared in the same customer journey.
        </p>
      </div>
      <div className="mt-8">
        <div>
          <h4 className="text-xs text-black">
            POSITION RELATIVE TO THIS CAMPAIGN
          </h4>
          <div className="flex items-center gap-4">
            <div className="w-64">
              <Select
                options={RelativePositionChoice.reactSelectOptions}
                value={relativePosition}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Flyout",
                    action: "onSelect",
                    additionalActionInfo:
                      "Flyout Journey change relative position",
                    data: {
                      newRelativePosition: value,
                      pageContext: { relativePosition },
                    },
                  });

                  setRelativePosition(value);
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                value={isBreakdownSelectVisible}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Flyout",
                    action: "onClick",
                    additionalActionInfo:
                      "Flyout Journey segment by breakdown label",
                    data: {
                      newIsBreakdownSelectVisible: value,
                      pageContext: { isBreakdownSelectVisible },
                    },
                  });

                  setIsBreakdownSelectVisible(value);
                  setSelectedLabel("");
                }}
              />
              <div className="text-xs">Segment journey by breakdown label</div>
            </div>
          </div>
        </div>
        {isBreakdownSelectVisible && (
          <div className="mt-4">
            <h4 className="text-xs text-black">BREAKDOWN</h4>
            <div className="w-64">
              <Select
                options={labelOtions}
                value={selectedLabel}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Flyout",
                    action: "onSelect",
                    additionalActionInfo: "Flyout Journey select breakdown",
                    data: {
                      newSelectedLabel: value,
                      pageContext: { selectedLabel },
                    },
                  });

                  setSelectedLabel(value);
                }}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-8">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold m-0">({rows.length}) Journeys</h3>
        </div>
        {selectedLabel === "" && (
          <div className="text-xxs flex gap-5 justify-end mb-5">
            <div className="flex items-center gap-1">
              <div className="bg-cosmic-3 border border-cosmic rounded w-4 h-4"></div>
              This campaign
            </div>
            <div className="flex items-center gap-1">
              <div className="bg-venus-2 border border-venus rounded w-4 h-4"></div>
              Any other campaign
            </div>
            <div className="flex items-center gap-1">
              <div className="bg-sun-2 border border-sun rounded w-4 h-4"></div>
              Organic search
            </div>
            <div className="flex items-center gap-1">
              <div className="bg-pluto-2 border border-pluto rounded w-4 h-4"></div>
              Direct visit
            </div>
          </div>
        )}
        <div className="h-[35rem] border border-gray rounded-lg overflow-hidden">
          <Table rows={rows} />
        </div>
      </div>
    </>
  );
};
